import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '../widgets/Dialog';

import { PageWrapper, PageHeader, DeleteDialog } from '../partials';
import css from './TrackingLinksMain.css';
import App from './TrackingLinks/App';
import Filters from './TrackingLinks/Filters';
import Search from './TrackingLinks/Search';
import Spinner from '../widgets/Spinner';
import RadioButtonsGroup from '../widgets/RadioButtonsGroup';
import { downloadCSV } from '../../utils/trackingLinks';
import NoLinks from '../../resources/svg/icon_no_links.svg';

const TrackingLinksMain = ({
    trackingLinks = [],
    filteredTrackingLinks = [],
    campaign_id_to_delete: campaignIdToDelete = null,
    searchOptions = [],
    filterType = '',
    filters = [],
    filterSuggestions = [],
    search = '',
    fetching = false,
    expanded = [],
    filteredApps = [],
    expandedNetworks = {},
    expandedCampaigns = {},
    editingCampaign = null,
    enabledCampaignEditing = false,
    addFilter = () => {},
    removeFilter = () => {},
    changeFilter = () => {},
    addFilterTag = () => {},
    removeFilterTag = () => {},
    clearFilterTags = () => {},
    collapseAll = () => {},
    expandAll = () => {},
    toggleExpandApp = () => {},
    toggleExpandNetwork = () => {},
    toggleExpandCampaign = () => {},
    handleFilterSelector = () => {},
    updateSearch = () => {},
    searchTrackingLink = () => {},
    deleteClicked = () => {},
    deleteConfirmed = () => {},
    deleteDismissed = () => {},
    editClicked = () => {},
    editCampaignName = () => {},
}) => {
    const exportCSV = () => {
        downloadCSV(filteredTrackingLinks);
    };

    const handleEditCampaignName = (app, e) => {
        if (e.keyCode === 13) {
            const newCampaignName = e.target.value;
            const campaign = trackingLinks.find(l => `${l.campaign_id}` === `${editingCampaign}`);
            editCampaignName(editingCampaign, newCampaignName, campaign, app);
        }
    };
    return (
        <PageWrapper>
            <PageHeader text="STATIC.TRACKING_LINKS" leftComponents={[]} rightComponent={[]} />
            <div>
                To learn more, see{' '}
                <a href="https://support.singular.net/hc/en-us/articles/360015221071">Editing and Archiving Links</a>
            </div>
            <div className={css.FiltersWrapper}>
                <div className={css.HeaderControls}>
                    <RadioButtonsGroup radioItems={searchOptions} horizontal onChange={handleFilterSelector} />
                    <button className={`${css.ExportCsv}`} onClick={exportCSV}>
                        Export
                    </button>
                </div>
                {filterType === 'dimensions' && (
                    <Filters
                        css={css}
                        filters={filters}
                        suggestions={filterSuggestions}
                        addFilter={addFilter}
                        removeFilter={removeFilter}
                        changeFilter={changeFilter}
                        addFilterTag={addFilterTag}
                        removeFilterTag={removeFilterTag}
                        clearFilterTags={clearFilterTags}
                    />
                )}
                {filterType === 'link' && (
                    <Search css={css} search={search} onChange={updateSearch} onSubmit={searchTrackingLink} />
                )}
            </div>
            <hr className={css.Divider} />

            <Dialog
                modal
                open={campaignIdToDelete !== undefined && campaignIdToDelete !== null}
                repositionOnUpdate={false}
                style={{
                    paddingTop: 16,
                }}
            >
                <DeleteDialog
                    style={{ textAlign: 'center' }}
                    onAccept={() => {
                        deleteConfirmed(campaignIdToDelete);
                    }}
                    onReject={deleteDismissed}
                    acceptText="STATIC.PAGES.VIEW_TRACKING_LINKS.ARCHIVE_YES"
                    title="STATIC.PAGES.VIEW_TRACKING_LINKS.DELETE_WARNING"
                    bodyClassName={css.modal}
                />
            </Dialog>

            <Spinner style={{ marginTop: fetching ? '300px' : 0 }} show={fetching} />
            {!fetching && filteredTrackingLinks.length > 0 && (
                <div className={css.AppsWrapper}>
                    <div className={css.AvailableLinks}>
                        <h5>{`Available Links (${filteredTrackingLinks.length})`}</h5>
                    </div>
                    {expanded.length > 0 && (
                        <div className={css.CollapseAll} onClick={collapseAll}>
                            Collapse All
                        </div>
                    )}
                    {expanded.length === 0 && (
                        <div className={css.CollapseAll} onClick={expandAll}>
                            Expand All
                        </div>
                    )}
                    <div style={{ margin: '20px 0px 10px 0px' }}>
                        {filteredApps.map(app => (
                            <App
                                app={app}
                                css={css}
                                key={app.id}
                                toggleExpandApp={() => {
                                    toggleExpandApp(app.id);
                                }}
                                toggleExpandNetwork={toggleExpandNetwork}
                                toggleExpandCampaign={toggleExpandCampaign}
                                expanded={expanded.indexOf(app.id) > -1}
                                expandedNetworks={expandedNetworks[app.id] || []}
                                expandedCampaigns={expandedCampaigns}
                                deleteClicked={deleteClicked}
                                editCampaignName={handleEditCampaignName}
                                editClicked={editClicked}
                                editingCampaign={editingCampaign}
                                enabledCampaignEditing={enabledCampaignEditing}
                            />
                        ))}
                    </div>
                </div>
            )}
            {!fetching && filteredTrackingLinks.length === 0 && trackingLinks.length > 0 && (
                <div
                    style={{
                        width: '100%',
                        textAlign: 'center',
                        marginTop: '50px',
                        color: '#0F172A',
                        fontSize: '16px',
                    }}
                >
                    No matching links found
                    <br />
                    <NoLinks style={{ marginTop: '20px', width: '38px', height: '48px' }} />
                </div>
            )}
            {!fetching && trackingLinks.length === 0 && (
                <div
                    style={{
                        width: '100%',
                        textAlign: 'center',
                        marginTop: '50px',
                        color: '#0F172A',
                        fontSize: '16px',
                    }}
                >
                    You have not configured any Tracking Links. You can configure them in the&nbsp;
                    <a target="_blank" href="/react/link-management//create-link">
                        Create Link
                    </a>{' '}
                    or{' '}
                    <a target="_blank" href="/react/link-management/create-smart-link">
                        Create Custom Link
                    </a>{' '}
                    pages
                </div>
            )}
        </PageWrapper>
    );
};

TrackingLinksMain.propTypes = {
    trackingLinks: PropTypes.arrayOf(PropTypes.any),
    filteredTrackingLinks: PropTypes.arrayOf(PropTypes.any),
    campaign_id_to_delete: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    searchOptions: PropTypes.arrayOf(PropTypes.any),
    filterType: PropTypes.string,
    filters: PropTypes.arrayOf(PropTypes.any),
    filterSuggestions: PropTypes.arrayOf(PropTypes.any),
    search: PropTypes.string,
    fetching: PropTypes.bool,
    expanded: PropTypes.arrayOf(PropTypes.any),
    filteredApps: PropTypes.arrayOf(PropTypes.any),
    expandedNetworks: PropTypes.objectOf(PropTypes.any),
    expandedCampaigns: PropTypes.objectOf(PropTypes.any),
    editingCampaign: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    enabledCampaignEditing: PropTypes.bool,
    addFilter: PropTypes.func,
    removeFilter: PropTypes.func,
    changeFilter: PropTypes.func,
    addFilterTag: PropTypes.func,
    removeFilterTag: PropTypes.func,
    clearFilterTags: PropTypes.func,
    collapseAll: PropTypes.func,
    expandAll: PropTypes.func,
    toggleExpandApp: PropTypes.func,
    toggleExpandNetwork: PropTypes.func,
    toggleExpandCampaign: PropTypes.func,
    handleFilterSelector: PropTypes.func,
    updateSearch: PropTypes.func,
    searchTrackingLink: PropTypes.func,
    deleteClicked: PropTypes.func,
    deleteConfirmed: PropTypes.func,
    deleteDismissed: PropTypes.func,
    editClicked: PropTypes.func,
    editCampaignName: PropTypes.func,
};

export default TrackingLinksMain;
